import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { createClient } from "@supabase/supabase-js";
import type { Database } from "@/integrations/supabase/types";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { assertBetaAccess } from "@/lib/beta-access";
import { requireCompany } from "@/lib/company-access";
import { SERVICE_COMMISSION_RATE } from "@/lib/service-categories";
import type { StripeEnv } from "@/lib/stripe.server";
import { assertPublicRateLimit } from "@/lib/rate-limit.server";

// Service Marketplace: customers find providers by category ("painter"),
// request a non-binding quote/appointment, the provider quotes, the customer
// pays online through RTN — the platform keeps a 10% commission and the
// provider is paid out the remaining 90%.

const SITE = "https://realitytwin.io";
const CURRENCIES = ["EUR", "CHF", "USD"] as const;

function publicClient() {
  return createClient<Database>(process.env.SUPABASE_URL!, process.env.SUPABASE_PUBLISHABLE_KEY!, {
    auth: { storage: undefined, persistSession: false, autoRefreshToken: false },
  });
}

// ---------- Public: provider directory ----------

export const listServiceProviders = createServerFn({ method: "POST" })
  .inputValidator((d) =>
    z
      .object({
        category: z.string().trim().max(60).optional(),
        country: z.string().trim().max(80).optional(),
        q: z.string().trim().max(120).optional(),
      })
      .partial()
      .parse(d ?? {}),
  )
  .handler(async ({ data }) => {
    await assertPublicRateLimit("services:list", 60);
    const supabase = publicClient();
    let query = supabase
      .from("companies")
      .select("id, name, description, industry, country, logo_url, service_categories, ai_score")
      .eq("is_public", true)
      .eq("offers_services", true)
      .order("ai_score", { ascending: false })
      .limit(60);
    if (data.category) query = query.contains("service_categories", [data.category]);
    if (data.country) query = query.ilike("country", `%${data.country}%`);
    if (data.q) query = query.ilike("name", `%${data.q}%`);
    const { data: rows, error } = await query;
    if (error) throw error;
    const providers = rows ?? [];

    // Aggregate public ratings for the listed providers in one query.
    let ratings = new Map<string, { sum: number; count: number }>();
    if (providers.length > 0) {
      const { data: reviewRows } = await supabase
        .from("service_reviews")
        .select("company_id, rating")
        .in(
          "company_id",
          providers.map((p) => p.id),
        );
      for (const r of reviewRows ?? []) {
        const agg = ratings.get(r.company_id) ?? { sum: 0, count: 0 };
        agg.sum += r.rating;
        agg.count += 1;
        ratings.set(r.company_id, agg);
      }
    }
    return {
      providers: providers.map((p) => {
        const agg = ratings.get(p.id);
        return {
          ...p,
          rating_avg: agg ? Math.round((agg.sum / agg.count) * 10) / 10 : null,
          rating_count: agg?.count ?? 0,
        };
      }),
    };
  });

// ---------- Public: post-job review (via the same single-use token) ----------

export const submitServiceReview = createServerFn({ method: "POST" })
  .inputValidator((d) =>
    z
      .object({
        token: z.string().uuid(),
        rating: z.number().int().min(1).max(5),
        comment: z.string().trim().max(1000).optional(),
      })
      .parse(d),
  )
  .handler(async ({ data }) => {
    await assertPublicRateLimit("services:review", 5);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: request } = await supabaseAdmin
      .from("service_requests")
      .select("id, company_id, customer_name, status")
      .eq("public_token", data.token)
      .maybeSingle();
    if (!request || !["paid", "completed"].includes(request.status)) {
      throw new Error("Reviews are possible after the job was paid.");
    }
    const { error } = await supabaseAdmin.from("service_reviews").insert({
      request_id: request.id,
      company_id: request.company_id,
      rating: data.rating,
      comment: data.comment || null,
      customer_name: request.customer_name,
    });
    if (error) {
      if ((error as { code?: string }).code === "23505") {
        throw new Error("This job was already reviewed.");
      }
      throw error;
    }
    return { ok: true };
  });

// ---------- Public: customer submits a request ----------

export const submitServiceRequest = createServerFn({ method: "POST" })
  .inputValidator((d) =>
    z
      .object({
        companyId: z.string().uuid(),
        name: z.string().trim().min(1).max(120),
        email: z.string().trim().email().max(200),
        category: z.string().trim().max(60).optional(),
        message: z.string().trim().min(5).max(2000),
        preferredDate: z.string().trim().max(120).optional(),
        budget: z.number().nonnegative().finite().optional(),
        // Honeypot — real users never fill this hidden field.
        website: z.string().max(0).optional(),
      })
      .parse(d),
  )
  .handler(async ({ data }) => {
    await assertPublicRateLimit("services:request", 5);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: company } = await supabaseAdmin
      .from("companies")
      .select("id, user_id, name, is_public, offers_services")
      .eq("id", data.companyId)
      .maybeSingle();
    if (!company || !company.is_public || !company.offers_services) {
      throw new Error("Provider not found");
    }

    // Abuse guard: cap unauthenticated requests per provider per hour.
    const hourAgo = new Date(Date.now() - 3600_000).toISOString();
    const { count } = await supabaseAdmin
      .from("service_requests")
      .select("id", { count: "exact", head: true })
      .eq("company_id", company.id)
      .gte("created_at", hourAgo);
    if ((count ?? 0) >= 30) throw new Error("Too many requests, please try again later");

    const { data: request, error } = await supabaseAdmin
      .from("service_requests")
      .insert({
        company_id: company.id,
        provider_user_id: company.user_id,
        customer_name: data.name,
        customer_email: data.email,
        category: data.category ?? null,
        message: data.message,
        preferred_date: data.preferredDate ?? null,
        budget: data.budget ?? null,
      })
      .select("id")
      .single();
    if (error) throw error;

    // Notify the provider — a service request is the hottest kind of lead.
    try {
      const { enqueueTransactionalEmail, getUserEmail, baseEmailTemplate, isEmailPrefEnabled } =
        await import("@/lib/notify.server");
      if (await isEmailPrefEnabled(company.user_id, "email_leads")) {
        const to = await getUserEmail(company.user_id);
        if (to) {
          await enqueueTransactionalEmail({
            to,
            subject: `New service request: ${data.category ?? "inquiry"} from ${data.name}`,
            html: baseEmailTemplate(
              "New service request",
              `<p><strong>${data.name}</strong> (${data.email}) asks for <strong>${data.category ?? "your services"}</strong>:</p><p style="margin-top:12px;padding:12px 16px;background:#15151c;border-radius:10px;white-space:pre-wrap">${data.message.replace(/</g, "&lt;")}</p>${data.preferredDate ? `<p>Preferred date: <strong>${data.preferredDate}</strong></p>` : ""}${data.budget ? `<p>Budget: <strong>${data.budget}</strong></p>` : ""}<p>Send a quote — the customer can pay online immediately.</p>`,
              { url: `${SITE}/requests`, label: "Quote now" },
            ),
            text: `New service request from ${data.name} (${data.email}): ${data.message}. Quote at ${SITE}/requests`,
            label: "service_request",
          });
        }
      }
      const { dispatchLeadWebhook } = await import("@/lib/webhooks.server");
      await dispatchLeadWebhook(company.id, {
        name: data.name,
        email: data.email,
        question: data.message.slice(0, 500),
        intent_score: 95,
        lead_score: 95,
        status: "hot",
        source: "service_request",
      });
    } catch (e) {
      console.error("service request notification failed:", e);
    }

    return { ok: true, requestId: request.id };
  });

// ---------- Provider: inbox, quote, decline, complete ----------

export const listServiceRequests = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await assertBetaAccess(context.supabase, context.userId);
    const access = await requireCompany(context.supabase, context.userId).catch(() => null);
    if (!access) return { requests: [], offersServices: false };
    const [{ data: requests }, { data: company }] = await Promise.all([
      context.supabase
        .from("service_requests")
        .select(
          "id, customer_name, customer_email, category, message, preferred_date, budget, currency, status, quoted_amount, quoted_note, commission_amount, paid_at, created_at",
        )
        .eq("company_id", access.id)
        .order("created_at", { ascending: false })
        .limit(200),
      context.supabase
        .from("companies")
        .select("offers_services")
        .eq("id", access.id)
        .maybeSingle(),
    ]);
    return { requests: requests ?? [], offersServices: !!company?.offers_services };
  });

export const quoteServiceRequest = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d) =>
    z
      .object({
        id: z.string().uuid(),
        amount: z.number().positive().max(1_000_000),
        currency: z.enum(CURRENCIES).default("EUR"),
        note: z.string().trim().max(1000).optional(),
      })
      .parse(d),
  )
  .handler(async ({ context, data }) => {
    await assertBetaAccess(context.supabase, context.userId);
    const access = await requireCompany(context.supabase, context.userId);

    // No quote without complete legal + payout details — otherwise there is
    // nowhere to send the 90% payout and no legal block for the receipt.
    const { isPayoutReady } = await import("@/lib/billing-profile.functions");
    const { data: profile } = await context.supabase
      .from("company_billing_profiles")
      .select("legal_name, address, city, payout_iban, payout_holder")
      .eq("company_id", access.id)
      .maybeSingle();
    if (!isPayoutReady(profile)) {
      throw new Error(
        "Complete your business & payout details (legal name, address, IBAN) in Settings → Company before quoting paid jobs.",
      );
    }

    const commission = Math.round(data.amount * SERVICE_COMMISSION_RATE * 100) / 100;

    const { data: updated, error } = await context.supabase
      .from("service_requests")
      .update({
        status: "quoted",
        quoted_amount: data.amount,
        quoted_note: data.note ?? null,
        currency: data.currency,
        commission_amount: commission,
        updated_at: new Date().toISOString(),
      })
      .eq("id", data.id)
      .eq("company_id", access.id)
      .in("status", ["new", "quoted"])
      .select("id, public_token, customer_name, customer_email")
      .maybeSingle();
    if (error) throw error;
    if (!updated) throw new Error("Request not found or already paid");

    // Send the customer their quote with a secure pay link.
    try {
      const { data: company } = await context.supabase
        .from("companies")
        .select("name")
        .eq("id", access.id)
        .maybeSingle();
      const { enqueueTransactionalEmail, baseEmailTemplate } = await import("@/lib/notify.server");
      const payUrl = `${SITE}/services/pay/${updated.public_token}`;
      await enqueueTransactionalEmail({
        to: updated.customer_email,
        subject: `Your quote from ${company?.name ?? "the provider"}: ${data.amount} ${data.currency}`,
        html: baseEmailTemplate(
          "Your quote is ready",
          `<p>Hi ${updated.customer_name},</p><p><strong>${company?.name ?? "The provider"}</strong> quoted <strong>${data.amount} ${data.currency}</strong> for your request.</p>${data.note ? `<p style="margin-top:12px;padding:12px 16px;background:#15151c;border-radius:10px;white-space:pre-wrap">${data.note.replace(/</g, "&lt;")}</p>` : ""}<p>Review and pay securely online — the job is confirmed the moment payment completes.</p>`,
          { url: payUrl, label: "View quote & pay" },
        ),
        text: `${company?.name ?? "The provider"} quoted ${data.amount} ${data.currency}. Pay securely: ${payUrl}`,
        label: "service_quote",
      });
    } catch (e) {
      console.error("quote email failed:", e);
    }

    return { ok: true };
  });

export const declineServiceRequest = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d) => z.object({ id: z.string().uuid() }).parse(d))
  .handler(async ({ context, data }) => {
    await assertBetaAccess(context.supabase, context.userId);
    const access = await requireCompany(context.supabase, context.userId);
    const { error } = await context.supabase
      .from("service_requests")
      .update({ status: "declined", updated_at: new Date().toISOString() })
      .eq("id", data.id)
      .eq("company_id", access.id)
      .in("status", ["new", "quoted"]);
    if (error) throw error;
    return { ok: true };
  });

export const completeServiceRequest = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d) => z.object({ id: z.string().uuid() }).parse(d))
  .handler(async ({ context, data }) => {
    await assertBetaAccess(context.supabase, context.userId);
    const access = await requireCompany(context.supabase, context.userId);
    const { error } = await context.supabase
      .from("service_requests")
      .update({ status: "completed", updated_at: new Date().toISOString() })
      .eq("id", data.id)
      .eq("company_id", access.id)
      .eq("status", "paid");
    if (error) throw error;
    return { ok: true };
  });

// ---------- Public: quote view + checkout + confirmation ----------

export const getServiceRequestByToken = createServerFn({ method: "POST" })
  .inputValidator((d) => z.object({ token: z.string().uuid() }).parse(d))
  .handler(async ({ data }) => {
    await assertPublicRateLimit("services:token", 30);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: request } = await supabaseAdmin
      .from("service_requests")
      .select(
        "id, customer_name, category, message, preferred_date, status, quoted_amount, quoted_note, currency, paid_at, company_id, companies(name, logo_url, country)",
      )
      .eq("public_token", data.token)
      .maybeSingle();
    if (!request || !["quoted", "paid", "completed"].includes(request.status)) {
      throw new Error("Quote not found or not ready yet");
    }
    // Legal block for the customer (never the IBAN — that stays private).
    const { data: legal } = await supabaseAdmin
      .from("company_billing_profiles")
      .select("legal_name, address, postal_code, city, country, vat_id")
      .eq("company_id", request.company_id)
      .maybeSingle();
    const { data: existingReview } = await supabaseAdmin
      .from("service_reviews")
      .select("id")
      .eq("request_id", request.id)
      .maybeSingle();
    const r = request as any;
    return {
      request: {
        ...r,
        provider_name: r.companies?.name ?? "Provider",
        provider_logo: r.companies?.logo_url ?? null,
        provider_country: r.companies?.country ?? null,
        reviewed: !!existingReview,
        provider_legal: legal
          ? {
              legal_name: legal.legal_name,
              address: legal.address,
              postal_code: legal.postal_code,
              city: legal.city,
              country: legal.country,
              vat_id: legal.vat_id,
            }
          : null,
        companies: undefined,
      },
    };
  });

export const createServiceCheckout = createServerFn({ method: "POST" })
  .inputValidator((d) =>
    z
      .object({
        token: z.string().uuid(),
        environment: z.enum(["sandbox", "live"]),
      })
      .parse(d),
  )
  .handler(async ({ data }) => {
    await assertPublicRateLimit("services:checkout", 10);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: request } = await supabaseAdmin
      .from("service_requests")
      .select("id, status, quoted_amount, currency, customer_email, category, companies(name)")
      .eq("public_token", data.token)
      .maybeSingle();
    if (!request || request.status !== "quoted" || !request.quoted_amount) {
      throw new Error("This quote cannot be paid (already paid or withdrawn).");
    }

    const { createStripeClient, getStripeErrorMessage } = await import("@/lib/stripe.server");
    try {
      const stripe = createStripeClient(data.environment as StripeEnv);
      const providerName = (request as any).companies?.name ?? "Provider";
      const session = await stripe.checkout.sessions.create({
        mode: "payment",
        line_items: [
          {
            price_data: {
              currency: request.currency.toLowerCase(),
              product_data: {
                name: `${request.category ?? "Service"} — ${providerName}`,
                description: `Booked via Reality Twin Network (incl. platform fee)`,
              },
              unit_amount: Math.round(Number(request.quoted_amount) * 100),
            },
            quantity: 1,
          },
        ],
        customer_email: request.customer_email,
        success_url: `${SITE}/services/pay/${data.token}?session_id={CHECKOUT_SESSION_ID}`,
        cancel_url: `${SITE}/services/pay/${data.token}`,
        metadata: { service_request_id: request.id },
        managed_payments: { enabled: true },
      } as any);
      await supabaseAdmin
        .from("service_requests")
        .update({ stripe_session_id: session.id, updated_at: new Date().toISOString() })
        .eq("id", request.id);
      if (!session.url) throw new Error("Stripe did not return a checkout URL");
      return { url: session.url };
    } catch (e) {
      throw new Error(getStripeErrorMessage(e));
    }
  });

export const confirmServicePayment = createServerFn({ method: "POST" })
  .inputValidator((d) =>
    z
      .object({
        token: z.string().uuid(),
        sessionId: z.string().min(5).max(200),
        environment: z.enum(["sandbox", "live"]),
      })
      .parse(d),
  )
  .handler(async ({ data }) => {
    await assertPublicRateLimit("services:confirm", 30);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: request } = await supabaseAdmin
      .from("service_requests")
      .select(
        "id, status, company_id, provider_user_id, customer_name, customer_email, quoted_amount, commission_amount, currency, category, companies(name)",
      )
      .eq("public_token", data.token)
      .maybeSingle();
    if (!request) throw new Error("Request not found");
    if (request.status === "paid" || request.status === "completed") return { ok: true };

    const { createStripeClient, getStripeErrorMessage } = await import("@/lib/stripe.server");
    let paid = false;
    try {
      const stripe = createStripeClient(data.environment as StripeEnv);
      const session = await stripe.checkout.sessions.retrieve(data.sessionId);
      paid =
        session.metadata?.service_request_id === request.id &&
        session.status === "complete" &&
        session.payment_status === "paid";
    } catch (e) {
      throw new Error(getStripeErrorMessage(e));
    }
    if (!paid) throw new Error("Payment not completed yet");

    await supabaseAdmin
      .from("service_requests")
      .update({
        status: "paid",
        stripe_session_id: data.sessionId,
        paid_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      })
      .eq("id", request.id);

    const amount = Number(request.quoted_amount ?? 0);
    const commission = Number(request.commission_amount ?? 0);

    // Record the debt. The money is in the platform's Stripe account and the
    // provider's share has to be transferred by bank — telling them an amount
    // without recording the obligation left nothing to actually pay against.
    const { recordPayout } = await import("@/lib/payouts.functions");
    const payout = await recordPayout({
      companyId: request.company_id,
      userId: request.provider_user_id,
      source: "service_request",
      sourceId: request.id,
      currency: request.currency,
      grossCents: Math.round(amount * 100),
      feeCents: Math.round(commission * 100),
    });

    // Tell the provider the job is funded, and say plainly how the payout works
    // rather than implying it already happened.
    try {
      const { enqueueTransactionalEmail, getUserEmail, baseEmailTemplate } =
        await import("@/lib/notify.server");
      const to = await getUserEmail(request.provider_user_id);
      const net = (amount - commission).toFixed(2);
      if (to) {
        const payoutLine = payout.blocked
          ? `<p style="padding:12px 16px;background:#2a1f10;border-radius:10px"><strong>We cannot pay you yet.</strong> There is no payout account on your profile. Add your IBAN and the transfer will be released.</p>`
          : `<p><strong>${net} ${request.currency}</strong> is now owed to you (after the 10% Reality Twin fee of ${commission.toFixed(2)} ${request.currency}). It is transferred to your account on file by bank — you can follow its status under Services in your Twin.</p>`;
        await enqueueTransactionalEmail({
          to,
          subject: `Paid: ${request.customer_name} booked ${request.category ?? "your service"} (${amount} ${request.currency})`,
          html: baseEmailTemplate(
            "Job booked & paid",
            `<p><strong>${request.customer_name}</strong> (${request.customer_email}) paid <strong>${amount} ${request.currency}</strong> online.</p>${payoutLine}<p>Reach out to schedule the appointment.</p>`,
            { url: `${SITE}/requests`, label: "Open request" },
          ),
          text: `${request.customer_name} paid ${amount} ${request.currency}. ${payout.blocked ? "Add your IBAN so we can pay you." : `${net} ${request.currency} is owed to you and will be transferred by bank.`}`,
          label: "service_paid",
        });
      }
    } catch (e) {
      console.error("paid notification failed:", e);
    }

    return { ok: true };
  });
